import {
	BrandPalette,
	LikedItem,
	Motivations,
	NCCluster,
	PositioningLane,
	PositioningOutputSnapshot,
	PositioningOutputType,
	UserPersona,
} from './commonTypes';
import { displayablePersonaTheme, isEncodedPromptTheme } from './controllers/api/boards/generateUtils';

const compactText = (value: string): string =>
	value
		.replace(/<[^>]*>/g, ' ')
		.replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
		.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
		.replace(/[*_~`>#]/g, '')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&amp;/gi, '&')
		.replace(/&lt;/gi, '<')
		.replace(/&gt;/gi, '>')
		.replace(/&quot;/gi, '"')
		.replace(/&#39;/gi, "'")
		.replace(/\s+/g, ' ')
		.trim();

const firstSentence = (value: string): string => {
	const text = compactText(value);
	if (!text) return '';
	return text.match(/^(.+?[.!?])(?:\s|$)/)?.[1] || text;
};

const fallbackHash = (value: string): string => {
	let hash = 2166136261;
	for (let index = 0; index < value.length; index += 1) {
		hash ^= value.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return (hash >>> 0).toString(36);
};

const sourceLikeIdFor = (item: LikedItem): string | undefined =>
	item._id === undefined ? undefined : String(item._id);

export const getLikedItemSourceOutputId = (item: LikedItem): string =>
	item.sourceOutputId ||
	item.clientId ||
	(sourceLikeIdFor(item) ? `like:${sourceLikeIdFor(item)}` : undefined) ||
	`legacy:${fallbackHash([item.type, item.title, item.text, item.url].filter(Boolean).join('|'))}`;

const parseJson = <T>(value: string | undefined): T | null => {
	if (!value) return null;
	try {
		const withoutFence = value
			.trim()
			.replace(/^```(?:json)?\s*/i, '')
			.replace(/\s*```$/, '');
		const parsed = JSON.parse(withoutFence) as unknown;
		return (typeof parsed === 'string' ? JSON.parse(parsed) : parsed) as T;
	} catch {
		return null;
	}
};

const parseRecord = (value: string | undefined): Record<string, unknown> | null => {
	const parsed = parseJson<unknown>(value);
	return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
		? (parsed as Record<string, unknown>)
		: null;
};

const recordValue = (record: Record<string, unknown>, keys: string[]): string => {
	for (const key of keys) {
		const value = record[key];
		if (typeof value === 'string' || typeof value === 'number') return compactText(String(value));
	}
	return '';
};

const humaniseKey = (key: string): string =>
	key
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/[-_]+/g, ' ')
		.replace(/^./, (character) => character.toUpperCase());

const textSnapshot = ({
	item,
	baseId,
	version,
	type,
	lane,
	eyebrow,
	fallbackTitle,
}: {
	item: LikedItem;
	baseId: string;
	version: number;
	type: PositioningOutputType;
	lane?: PositioningLane;
	eyebrow: string;
	fallbackTitle: string;
}): PositioningOutputSnapshot => {
	const record = parseRecord(item.text);
	const text = record
		? recordValue(record, [
				'summary',
				'description',
				'rationale',
				'text',
				'value',
				'biography',
				'theme',
			])
		: compactText(item.text || item.title || '');
	const recordTitle = record
		? recordValue(record, ['name', 'title', 'label', 'phrase', 'theme'])
		: '';
	const title =
		compactText(item.title || '') || recordTitle || firstSentence(text) || fallbackTitle;
	const duplicateValues = new Set([title, text].map((value) => compactText(value).toLowerCase()));
	const recordDetails = record
		? Object.entries(record)
				.filter(([key, value]) => {
					if (['name', 'title', 'label', 'phrase'].includes(key)) return false;
					if (typeof value !== 'string' && typeof value !== 'number') return false;
					return !duplicateValues.has(compactText(String(value)).toLowerCase());
				})
				.slice(0, 6)
				.map(([key, value]) => ({ title: humaniseKey(key), value: compactText(String(value)) }))
		: [];
	return {
		schemaVersion: 1,
		sourceOutputId: baseId,
		sourceLikeId: sourceLikeIdFor(item),
		version,
		type,
		sourceType: item.type,
		lane,
		eyebrow,
		title,
		summary: text,
		positioningContext: item.positioningContext,
		payload: record
			? { kind: 'record', data: record, ...(item.url ? { url: item.url } : {}) }
			: item.url && (type === 'reference' || type === 'image' || type === 'video')
				? type === 'reference'
					? { kind: 'reference', text, url: item.url }
					: { kind: 'media', url: item.url, isVideo: type === 'video' }
				: { kind: 'text', text },
		details: recordDetails,
	};
};

const readableSnapshot = (snapshot: PositioningOutputSnapshot): PositioningOutputSnapshot => {
	let title = compactText(snapshot.title);
	let summary = compactText(snapshot.summary);
	let subtitle = snapshot.subtitle ? compactText(snapshot.subtitle) : undefined;
	let imageUrl = snapshot.imageUrl;
	let details = snapshot.details
		.map((detail) => ({ title: compactText(detail.title), value: compactText(detail.value) }))
		.filter((detail) => Boolean(detail.title && detail.value));

	if (snapshot.payload.kind === 'persona') {
		const { persona } = snapshot.payload;
		const isCharacter = snapshot.type === 'character';
		title = compactText(persona.name) || title;
		subtitle = displayablePersonaTheme(persona.theme) || undefined;
		summary = firstSentence(persona.biography || persona.needs) || summary;
		if (isEncodedPromptTheme(summary)) summary = '';
		imageUrl = persona.imageUrl || imageUrl;
		details = [
			...(!isCharacter ? [{ title: 'Age group', value: persona.ageGroup || '25–34' }] : []),
			...(!isCharacter && persona.gender ? [{ title: 'Gender', value: persona.gender }] : []),
			{ title: isCharacter ? 'Character essence' : 'Who they are', value: persona.biography },
			{ title: isCharacter ? 'Dramatic want' : 'Goals', value: persona.goals },
			{ title: isCharacter ? 'Core drive' : 'Needs', value: persona.needs },
			{ title: isCharacter ? 'Conflict' : 'Pain points', value: persona.painPoints },
			{ title: isCharacter ? 'Story function' : 'Motivations', value: persona.motivations },
			{ title: isCharacter ? 'World & influences' : 'Influences', value: persona.influences },
		]
			.map((detail) => ({ ...detail, value: compactText(detail.value) }))
			.filter((detail) => Boolean(detail.value) && !isEncodedPromptTheme(detail.value));
	} else if (snapshot.payload.kind === 'record') {
		const recordTitle = recordValue(snapshot.payload.data, [
			'name',
			'title',
			'label',
			'phrase',
			'theme',
		]);
		const recordSummary = recordValue(snapshot.payload.data, [
			'summary',
			'description',
			'rationale',
			'text',
			'value',
			'biography',
		]);
		title ||= recordTitle;
		summary ||= recordSummary;
	}

	if (isEncodedPromptTheme(subtitle)) subtitle = undefined;
	if (isEncodedPromptTheme(summary)) summary = '';

	if (!summary) {
		summary = details.find((detail) => detail.value.toLowerCase() !== title.toLowerCase())?.value || '';
	}

	const duplicateText = new Set([title, summary].map((value) => value.toLowerCase()).filter(Boolean));
	details = details.filter((detail) => !duplicateText.has(detail.value.toLowerCase()));

	return { ...snapshot, title, subtitle, summary, imageUrl, details };
};

export const toReadablePositioningOutput = readableSnapshot;

const personaSnapshot = (
	item: LikedItem,
	baseId: string,
	version: number,
	isCharacter: boolean
): PositioningOutputSnapshot | null => {
	const persona = parseJson<UserPersona>(item.text);
	if (!persona?.name) {
		return textSnapshot({
			item,
			baseId,
			version,
			type: isCharacter ? 'character' : 'persona',
			lane: isCharacter ? 'story' : 'audience',
			eyebrow: isCharacter ? 'Character' : 'Persona',
			fallbackTitle: isCharacter ? 'Character' : 'Audience persona',
		});
	}
	return {
		schemaVersion: 1,
		sourceOutputId: baseId,
		sourceLikeId: sourceLikeIdFor(item),
		version,
		type: isCharacter ? 'character' : 'persona',
		sourceType: item.type,
		lane: isCharacter ? 'story' : 'audience',
		eyebrow: isCharacter ? 'Character' : 'Persona',
		title: persona.name,
		subtitle: displayablePersonaTheme(persona.theme) || undefined,
		summary: firstSentence(persona.biography || persona.needs),
		imageUrl: persona.imageUrl,
		positioningContext: item.positioningContext,
		payload: { kind: 'persona', persona },
		details: [
			...(!isCharacter ? [{ title: 'Age group', value: persona.ageGroup || '25–34' }] : []),
			...(!isCharacter && persona.gender ? [{ title: 'Gender', value: persona.gender }] : []),
			{ title: isCharacter ? 'Character essence' : 'Who they are', value: persona.biography },
			{ title: isCharacter ? 'Dramatic want' : 'Goals', value: persona.goals },
			{ title: isCharacter ? 'Core drive' : 'Needs', value: persona.needs },
			{ title: isCharacter ? 'Conflict' : 'Pain points', value: persona.painPoints },
			{ title: isCharacter ? 'Story function' : 'Motivations', value: persona.motivations },
			{ title: isCharacter ? 'World & influences' : 'Influences', value: persona.influences },
		].filter((detail) => Boolean(detail.value)),
	};
};

const motivationSnapshots = (
	item: LikedItem,
	baseId: string,
	version: number
): PositioningOutputSnapshot[] => {
	const motivations = parseJson<Motivations>(item.text);
	if (!motivations?.stakeholders?.length) {
		const fallback = textSnapshot({
			item,
			baseId,
			version,
			type: 'stakeholder-motivation',
			lane: 'motivation',
			eyebrow: 'Stakeholder motivation',
			fallbackTitle: 'Stakeholder motivation',
		});
		return fallback ? [fallback] : [];
	}
	return motivations.stakeholders.map((stakeholder, stakeholderIndex) => ({
		schemaVersion: 1,
		sourceOutputId: `${baseId}:motivation:${stakeholderIndex}`,
		sourceLikeId: sourceLikeIdFor(item),
		version,
		type: 'stakeholder-motivation',
		sourceType: item.type,
		lane: 'motivation',
		eyebrow: 'Stakeholder motivation',
		title: stakeholder.name || item.title || 'Stakeholder',
		subtitle: motivations.theme || motivations.intro,
		summary: compactText(stakeholder.description),
		imageUrl: stakeholder.imageUrl,
		positioningContext: item.positioningContext,
		payload: { kind: 'motivation', motivations, stakeholderIndex },
		details: [
			{ title: 'Motivation', value: stakeholder.description },
			{ title: 'Context', value: motivations.intro },
			{ title: 'Design implication', value: motivations.outro },
		].filter((detail) => Boolean(detail.value)),
	}));
};

const paletteSnapshot = (
	item: LikedItem,
	baseId: string,
	version: number
): PositioningOutputSnapshot | null => {
	const palette = parseJson<BrandPalette>(item.text);
	if (!palette?.colours?.length) return null;
	return {
		schemaVersion: 1,
		sourceOutputId: baseId,
		sourceLikeId: sourceLikeIdFor(item),
		version,
		type: 'brand-colours',
		sourceType: item.type,
		lane: 'brand',
		eyebrow: 'Brand colours',
		title: palette.name || item.title || 'Brand palette',
		summary: compactText(palette.rationale),
		positioningContext: item.positioningContext,
		payload: { kind: 'palette', palette },
		details: palette.colours.map((colour) => ({
			title: `${colour.role}: ${colour.name}`,
			value: colour.hex,
		})),
	};
};

const clusterSnapshot = (
	item: LikedItem,
	baseId: string,
	version: number
): PositioningOutputSnapshot | null => {
	const cluster = parseJson<NCCluster>(item.text);
	if (!cluster?.group) return null;
	return {
		schemaVersion: 1,
		sourceOutputId: baseId,
		sourceLikeId: sourceLikeIdFor(item),
		version,
		type: 'cluster',
		sourceType: item.type,
		eyebrow: 'Cluster',
		title: cluster.group,
		summary: cluster.values.join(', '),
		payload: { kind: 'cluster', cluster },
		details: [{ title: 'Cluster signals', value: cluster.values.join(', ') }],
	};
};

const mediaSnapshot = (
	item: LikedItem,
	baseId: string,
	version: number,
	isVideo: boolean
): PositioningOutputSnapshot | null => {
	const url = item.url || (!item.text?.includes(' ') ? item.text : undefined);
	if (!url) return null;
	return {
		schemaVersion: 1,
		sourceOutputId: baseId,
		sourceLikeId: sourceLikeIdFor(item),
		version,
		type: isVideo ? 'video' : 'image',
		sourceType: item.type,
		eyebrow: isVideo ? 'Video' : 'Image',
		title: compactText(item.title || '') || (isVideo ? 'Video reference' : 'Visual reference'),
		summary: compactText(item.text || item.title || ''),
		imageUrl: isVideo ? undefined : url,
		payload: { kind: 'media', url, isVideo },
		details: [],
	};
};

export const createPositioningOutputSnapshots = (
	item: LikedItem,
	options: { sourceOutputId?: string; version?: number } = {}
): PositioningOutputSnapshot[] => {
	const baseId = options.sourceOutputId || getLikedItemSourceOutputId(item);
	const version = Math.max(1, options.version || item.outputVersion || 1);
	if (item.type === 'persona' || item.type === 'character') {
		const output = personaSnapshot(item, baseId, version, item.type === 'character');
		return output ? [output] : [];
	}
	if (item.type === 'motivations') return motivationSnapshots(item, baseId, version);
	if (item.type === 'brand-colours') {
		const output = paletteSnapshot(item, baseId, version);
		return [
			output ||
				textSnapshot({
					item,
					baseId,
					version,
					type: 'brand-colours',
					lane: 'brand',
					eyebrow: 'Brand colours',
					fallbackTitle: 'Brand palette',
				}),
		];
	}
	if (item.type === 'cluster') {
		const output = clusterSnapshot(item, baseId, version);
		return [
			output ||
				textSnapshot({
					item,
					baseId,
					version,
					type: 'cluster',
					eyebrow: 'Cluster',
					fallbackTitle: 'Cluster',
				}),
		];
	}
	if (
		item.type === 'search-image' ||
		item.type === 'image-inspire' ||
		item.type === 'image-synthesise'
	) {
		const output = mediaSnapshot(item, baseId, version, false);
		return [
			output ||
				textSnapshot({
					item,
					baseId,
					version,
					type: 'image',
					eyebrow: 'Image',
					fallbackTitle: 'Visual reference',
				}),
		];
	}
	if (item.type === 'video-inspire') {
		const output = mediaSnapshot(item, baseId, version, true);
		return [
			output ||
				textSnapshot({
					item,
					baseId,
					version,
					type: 'video',
					eyebrow: 'Video',
					fallbackTitle: 'Video reference',
				}),
		];
	}

	let metadata: {
		type: PositioningOutputType;
		lane?: PositioningLane;
		eyebrow: string;
		fallbackTitle: string;
	};
	if (item.type === 'audience') {
		metadata = {
			type: 'audience',
			lane: 'audience',
			eyebrow: 'Audience insight',
			fallbackTitle: 'Target audience',
		};
	} else if (['brainstorm', 'product-idea', 'product-brief'].includes(item.type)) {
		metadata = {
			type: 'solution',
			lane: 'solution',
			eyebrow: 'Opportunity',
			fallbackTitle: 'Solution direction',
		};
	} else if (item.type === 'story' || item.type === 'synopsis') {
		metadata = {
			type: 'story',
			lane: 'story',
			eyebrow: 'Story direction',
			fallbackTitle: 'Story direction',
		};
	} else if (['brand-strategy', 'taglines', 'proverb', 'brand-tone'].includes(item.type)) {
		metadata = { type: 'brand', lane: 'brand', eyebrow: 'Brand', fallbackTitle: 'Brand direction' };
	} else if (item.type === 'search-page' || item.type === 'article') {
		metadata = { type: 'reference', eyebrow: 'Research reference', fallbackTitle: 'Reference' };
	} else if (item.type === 'node') {
		metadata = { type: 'concept', eyebrow: 'Concept', fallbackTitle: 'Concept' };
	} else {
		metadata = { type: 'text', eyebrow: 'Saved output', fallbackTitle: 'Saved output' };
	}
	const output = textSnapshot({ item, baseId, version, ...metadata });
	return [output];
};

const isStoredSnapshot = (value: unknown): value is PositioningOutputSnapshot => {
	if (!value || typeof value !== 'object') return false;
	const snapshot = value as Partial<PositioningOutputSnapshot>;
	return (
		snapshot.schemaVersion === 1 &&
		typeof snapshot.sourceOutputId === 'string' &&
		typeof snapshot.version === 'number' &&
		typeof snapshot.type === 'string' &&
		typeof snapshot.title === 'string' &&
		typeof snapshot.summary === 'string' &&
		Boolean(snapshot.payload)
	);
};

export const getPositioningOutputSnapshots = (item: LikedItem): PositioningOutputSnapshot[] => {
	const sourceLikeId = sourceLikeIdFor(item);
	const stored = (item.structuredOutputs || []).filter(isStoredSnapshot);
	const snapshots = stored.length ? stored : createPositioningOutputSnapshots(item);
	return snapshots.map((snapshot) =>
		readableSnapshot({
			...snapshot,
			sourceLikeId: sourceLikeId || snapshot.sourceLikeId,
			positioningContext: snapshot.positioningContext || item.positioningContext,
		})
	);
};
